import React, { Component } from "react";
import format from "date-fns/format";
import map from "lodash/map";
import gql from "graphql-tag";
import { Query, Mutation } from "react-apollo";

import { Container, Header, Table, Button, Dropdown } from "semantic-ui-react";
import { Loader } from "components/Loader";
import { GraphQLError } from "components/GraphQLError";
import InfiniteScroll from "react-infinite-scroller";
import { CATEGORIES_QUERY } from "../../../graphql/queries";
import { withApplicationId, withLanguage } from 'HOCs';

class ContentList extends Component {
  state = {
    offset: 0,
    limit: 20,
    categoryId: null,
    hasMore: true
  };

  handleEditClick = event => {
    this.props.history.push(`contents/edit/${event.target.id}?${this.props.languageQuery}`);
  };

  handleFilter = (event, { value }) => {
    this.setState({ categoryId: value || null });
  };

  renderTable() {
    const { limit, offset, hasMore, categoryId } = this.state;
    const {applicationId, language } = this.props
    const variables = { language, applicationId, limit, offset, categoryId };
    return (
      <Query
        query={CONTENTS_QUERY}
        variables={variables}
        fetchPolicy="network-only"
      >
        {({ data, loading, error, fetchMore, networkStatus }) => {
          if (loading) {
            return <Loader />;
          }

          if (error) {
            return (
              <div>
                <b>Unable to load contents</b>
                <GraphQLError error={error} />
              </div>
            );
          }
          return (
            <InfiniteScroll
              loadMore={() => {
                if (!data.contents) {
                  return;
                }

                fetchMore({
                  variables: { offset: data.contents.length },
                  updateQuery: (prev, { fetchMoreResult, variables }) => {
                    if (!fetchMoreResult) return prev;
                    if (!fetchMoreResult.contents.length) {
                      this.setState({ hasMore: false });
                    }
                    return {
                      ...prev,
                      contents: [...prev.contents, ...fetchMoreResult.contents]
                    };
                  }
                });
              }}
              hasMore={hasMore}
              loader={<Loader key="loader-key" />}
            >
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell width={4}>Title</Table.HeaderCell>
                    <Table.HeaderCell>Category</Table.HeaderCell>
                    <Table.HeaderCell>Content Type</Table.HeaderCell>
                    <Table.HeaderCell>Last Modified</Table.HeaderCell>
                    <Table.HeaderCell>Actions</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>{map(data.contents, this.renderRow)}</Table.Body>
              </Table>
            </InfiniteScroll>
          );
        }}
      </Query>
    );
  }

  renderRow = ({
    id,
    title,
    category,
    template,
    updated,
    isPublished,
    webUrl
  }) => {
    const { limit, offset, categoryId } = this.state;
    const { language, applicationId } = this.props
    const variables = { language, applicationId, limit, offset, categoryId };
    return (
      <Table.Row key={id}>
        <Table.Cell>
          <a href={webUrl} target="_blank">
            {title}
          </a>
        </Table.Cell>
        <Table.Cell>{category}</Table.Cell>
        <Table.Cell>{template}</Table.Cell>
        <Table.Cell>
          <span>{format(updated, "MM/DD/YY, h:mm A")}</span>
        </Table.Cell>
        <Table.Cell>
          <Button id={id} primary onClick={this.handleEditClick}>
            Edit
          </Button>

          <Mutation
            mutation={DELETE_CONTENT}
            refetchQueries={[{ query: CONTENTS_QUERY, variables }]}
          >
            {(deleteContent, { loading }) => {
              if (loading) {
                return (
                  <Button negative disabled loading>
                    Deleting...
                  </Button>
                );
              }

              return (
                <Button
                  negative
                  onClick={() => {
                    deleteContent({ variables: { language, id } });
                  }}
                >
                  Delete
                </Button>
              );
            }}
          </Mutation>

          <Mutation
            mutation={UPDATE_CONTENT}
            refetchQueries={[{ query: CONTENTS_QUERY, variables }]}
          >
            {(updateContent, { loading }) => {
              if (loading) {
                return (
                  <Button color="orange" disabled loading>
                    Updating...
                  </Button>
                );
              }

              return (
                <Button
                  color="orange"
                  onClick={async () => {
                    await updateContent({
                      variables: {
                        language,
                        id,
                        input: { isPublished: !isPublished }
                      }
                    });
                  }}
                >
                  {isPublished ? "Unpublish" : "Publish"}
                </Button>
              );
            }}
          </Mutation>
        </Table.Cell>
      </Table.Row>
    );
  };

  render() {
    const { language, applicationId, languageQuery } = this.props
    return (
      <Container>
        <Header as="h1" content="Contents" />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Query
            query={CATEGORIES_QUERY}
            variables={{ language, applicationId }}
            fetchPolicy="network-only"
          >
            {({ data, loading, error }) => {
              return (
                <Dropdown
                  clearable
                  selection
                  placeholder="filter by content category"
                  options={
                    data &&
                    data.categories &&
                    data.categories.map(category => {
                      return {
                        key: category.id,
                        value: category.id,
                        text: category.name
                      };
                    })
                  }
                  onChange={this.handleFilter}
                  disabled={loading || !!error}
                  loading={loading}
                  error={!!error}
                />
              );
            }}
          </Query>
          <div style={{ display: "flex" }}>
            <Button
              primary
              onClick={() => this.props.history.push(`contents/new?${languageQuery}`)}
            >
              Add Content
            </Button>
          </div>
        </div>

        {this.renderTable()}
      </Container>
    );
  }
}

export const CONTENTS_QUERY = gql`
  query ContentsQuery(
    $language: String!
    $applicationId: ID!
    $offset: Int = 0
    $limit: Int = 20
    $categoryId: ID
  ) {
    contents(
      language: $language
      applicationId: $applicationId
      offset: $offset
      limit: $limit
      categoryId: $categoryId
    ) {
      id
      title
      category
      categoryId
      template
      updated
      isPublished
      webUrl
    }
  }
`;

export const UPDATE_CONTENT = gql`
  mutation UpdateContent(
    $language: String!
    $id: ID!
    $input: UpdateContentInput!
  ) {
    updateContent(language: $language, id: $id, input: $input) {
      id
    }
  }
`;

export const DELETE_CONTENT = gql`
  mutation DeleteContent($language: String!, $id: ID!) {
    deleteContent(language: $language, id: $id) {
      id
    }
  }
`;

export default withApplicationId(withLanguage(ContentList));



// WEBPACK FOOTER //
// ./src/pages/Contents/List/index.js